import type { DocPageContent } from '../docs-content'

const page = {
  order: 12,
  slug: 'audio',
  title: '音频编辑',
  description:
    '录制画外音、调整片段增益与淡变、音高、逐片段均衡、混音器与电平表，以及静音与填充词清理。',
  category: '创意工具',
  related: ['properties', 'local-ai'],
  sections: [
    {
      title: '调整片段',
      blocks: [
        {
          kind: 'paragraph',
          text: '选中一个片段并打开属性中的**音频**标签页。',
        },
        {
          kind: 'table',
          headers: ['控件', '范围'],
          rows: [
            ['增益', '-60 dB 到 +12 dB（−60 dB 实际上会使片段静音）'],
            ['淡入 / 淡出', '各 0 到 5 秒'],
            ['音高', '半音与音分，与速度无关'],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '你也可以直接拖动时间轴上片段的音量线与淡变手柄，并且**增益**可以打关键帧，使片段的音量逐渐升高或降低。',
        },
      ],
    },
    {
      title: '音高与音色',
      blocks: [
        {
          kind: 'list',
          items: [
            '**音高**控件可在不改变片段速度的情况下改变音高。若要改变速度，请使用视频标签页上的**速度**。',
            '**均衡器**部分为每段片段提供一个用于音色塑造的曲线编辑器。',
          ],
        },
      ],
    },
    {
      title: '混音器与电平表',
      blocks: [
        {
          kind: 'list',
          items: [
            '打开音频**电平表**以在播放时观察立体声电平并捕捉削波。',
            '使用**混音器**在项目中平衡各路电平。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '预览中的监视器音量仅影响本地播放，永远不会更改导出的混音。',
        },
      ],
    },
    {
      title: '录制画外音',
      blocks: [
        {
          kind: 'paragraph',
          text: '时间轴工具栏中的**录制画外音**（麦克风）按钮会与你的项目同步录制旁白。录制在**时间轴播放期间**进行，完成的片段会落在一条新的音频轨道上，锚定在你开始录制时的播放头位置。',
        },
        {
          kind: 'steps',
          items: [
            '将播放头移动到画外音应开始的位置。',
            '点击**麦克风**按钮——时间轴开始播放，录制随即开始。',
            '使用**暂停录制**可同时停止播放头与麦克风，使用**继续录制**继续，使用**停止并保存**完成录制并将片段放到新的音频轨道上。',
            '点击**取消录制**（✕）可丢弃该次录制，不添加任何内容。',
          ],
        },
        {
          kind: 'paragraph',
          text: '打开**麦克风设置**（按钮旁的 ▾）以选择输入设备并调整采集参数。那里的电平表是实时的，因此你可以在录制前检查麦克风。',
        },
        {
          kind: 'table',
          headers: ['设置', '作用'],
          rows: [
            ['噪声抑制', '减少稳定的背景噪声。默认开启。'],
            ['自动增益', '使你的声音保持在一致的电平。默认开启。'],
            [
              '录制时静音时间轴',
              '将时间轴监视器静音，使扬声器音频不会串入麦克风。默认开启。',
            ],
            [
              '同步偏移',
              '将录制的片段提前或推后，以补偿输入延迟（最多 ±1000 ms）。',
            ],
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '请使用耳机，这样项目音频绝不会被麦克风采集——然后你可以关闭**录制时静音时间轴**，在旁白时听到混音。',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '录制时禁用进度拖动，按下 `Space` 或到达时间轴结尾都会结束该次录制。这能让录制锁定在播放头上，从而永远不会失步。',
        },
      ],
    },
    {
      title: '清理对白',
      blocks: [
        {
          kind: 'list',
          items: [
            '**移除静音**会找出静音区间并剪掉它们；**最小静音时长**设置控制间隔必须达到多长，对话框会估算将被移除的内容量。',
            '**移除填充词**会从转录稿与音频中检测出诸如“um”“uh”之类的词并将其移除。',
            '两种工具都会显示它们将移除的区间，以便你在应用编辑前预览。',
          ],
        },
        {
          kind: 'note',
          tone: 'tip',
          text: '转录稿能提升填充词检测效果，因此请先生成转录稿以获得最佳结果。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
